import { useCallback, useEffect, useState } from "react";
import { listWarRoomWorkers } from "./warRoomApi.js";

// Prompt 141R3 raised the backend's hard pageSize cap from 100 to 500;
// Prompt 141R6 raises it again to 5000
// (Math.Clamp(pageSize ?? 25, 1, 5000) in WarRoomService.cs) specifically so
// the current seeded scope (2,500 people) fits on a single War Room page in
// one server round trip - this is the largest single page War Room ever
// requests, never a larger "load more at once" value and never a value the
// UI can push past that real server-side ceiling. WAR_ROOM_PAGE_SIZE_OPTIONS
// are the operator-facing choices, all of them at or below the real backend
// cap - selecting any of them still costs exactly one bounded request, never
// a background loop that prefetches the whole dataset.
export const WAR_ROOM_MAX_PAGE_SIZE = 5000;
export const WAR_ROOM_PAGE_SIZE_OPTIONS = [500, 1000, 2500, 5000];
export const WAR_ROOM_DEFAULT_PAGE_SIZE = 5000;

// Prompt 141R9: real server-side sorting (WarRoomService.NormalizeSortBy/
// NormalizeSortDirection) - "name" (global alphabetical, DisplayName COLLATE
// Thai_CI_AS) is the default so the seating board's row letters mean
// something real; "status"/"recent" are explicit operator choices only, the
// UI never silently switches to them.
export const WAR_ROOM_SORT_BY_OPTIONS = ["name", "status", "recent"];
export const WAR_ROOM_DEFAULT_SORT_BY = "name";
export const WAR_ROOM_DEFAULT_SORT_DIRECTION = "asc";

/**
 * Fetches GET /api/backoffice/war-room/workers with real server-side
 * pagination/filtering/sorting (Prompt 141's real roster source, replacing
 * the Prompt 135 Latest-Tracking-as-primary-roster wiring). Refetches every
 * time `filters` changes (page, pageSize, search, statusCategory,
 * applicationStatus, workerStatus, superAgentOrganizationId,
 * plcOrganizationId, agentId, sortBy, sortDirection), because a 200,000+
 * person table must never be
 * fetched in full just to filter/group client-side - only the current
 * page's rows (at most WAR_ROOM_DEFAULT_PAGE_SIZE) ever live in component
 * state. The response's own `summary` block (normal/warning/abnormal/
 * unknown/total over the FULL filtered result) is likewise never
 * recomputed client-side from the current page - it is used exactly as the
 * server returns it.
 *
 * `filters` must be a stable object reference (the caller should build it
 * with useMemo) - the effect depends on the object directly so it only
 * re-runs when a real filter value actually changes, not on every render.
 * Search debouncing is the caller's responsibility, not this hook's. This
 * hook never loops over pages to prefetch the whole dataset in the
 * background - it fetches exactly one page per call.
 */
export function useWarRoomWorkers(filters) {
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Previous page's data (if any) stays visible while a refetch is in
    // flight, so the seating board doesn't blank out on every filter change
    // or page turn - just an inline loading indicator over already-mounted
    // seats.
    setState((previous) => ({ status: "loading", data: previous.data, error: null }));

    listWarRoomWorkers(filters)
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState((previous) => ({ status: "error", data: previous.data, error }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filters, reloadToken]);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, refetch };
}
